import { HoldbackType } from '@/types/Holdbacks';
import { OrganizationNode } from '@/types/Organizations';
import { Box, SelectInputNative } from '@palmetto/palmetto-components';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { NavLink, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { ProgramType, ProgramTypeLabels } from 'types';

import { HoldbackSelfServiceTabOptions } from './HoldbackSelfServiceTabOptions';

const holdbackTabs = [
  { type: HoldbackType.standard, label: 'Standard' },
  { type: HoldbackType.domesticContent, label: 'Domestic Content' },
];

export const HoldbackSelfService = ({ organization }: { organization?: OrganizationNode }) => {
  const { alias, holdbackType, programType: urlProgramType } = useParams();
  const navigate = useNavigate();

  const [programType, setProgramType] = useState<ProgramType | undefined>(
    (urlProgramType as ProgramType | undefined) ?? organization?.programTypes?.[0],
  );

  const programTypesOptions = useMemo(() => {
    return organization?.programTypes?.map((pt) => ({ label: ProgramTypeLabels[pt], value: pt })) || [];
  }, [organization]);

  useEffect(() => {
    const isValidHoldbackType = holdbackTabs.some((tab) => tab.type === holdbackType);
    if (!holdbackType || !isValidHoldbackType) {
      navigate(HoldbackType.standard, { replace: true });
    }
  }, [holdbackType, navigate]);

  const handleProgramTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newProgramType = event.target.value as ProgramType;
    setProgramType(newProgramType);
    navigate(
      `/settings/organizations/${alias}/holdback-settings/${newProgramType}/${holdbackType ?? HoldbackType.standard}`,
    );
  };

  return (
    <Box>
      <Box direction="row" childGap="3xl">
        <Box>
          {holdbackTabs.map(({ type, label }) => (
            <NavLink
              to={`/settings/organizations/${alias}/holdback-settings/${urlProgramType ?? programType}/${type}`}
              style={{ textDecoration: 'none', fontWeight: '500' }}
              key={type}
              end
            >
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s', borderBottom: 0 }}
                >
                  {label}
                </Box>
              )}
            </NavLink>
          ))}
        </Box>
        <Box flex="auto">
          {organization?.programTypes?.length && organization?.programTypes?.length > 1 && (
            <Box maxWidth="2xl">
              <SelectInputNative
                id="required"
                label="Program Type"
                value={urlProgramType ?? programType ?? null}
                onChange={handleProgramTypeChange}
                options={programTypesOptions}
                menuPortalTarget={document.body}
              />
            </Box>
          )}
          <Routes>
            <Route path="/" element={<HoldbackSelfServiceTabOptions />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};
